import { useCallback, useMemo } from 'react';

import { CORE_PAGES } from '@core-ui/components';
import { API_URL, SubscriptionStatus } from '@moniebook-web/types';
import {
    Axios,
    SHARED_QUERY_KEY,
    useGetApiService,
    useUserInfo,
} from '@moniebook-web/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useLocation } from 'react-router';

import { useDismissBannerApi } from './api/use-dismiss-banner-api';
import type { BannerInfo, SubscriptionStatusDetails } from './types';
import { getBannerContent } from './utils';

const ONBOARDING_PATH = '/dashboard/onboarding';
const ADD_BUSINESS_PATH = '/dashboard/business';

const hideBannerByPath = (path: string) => {
    const paths = [
        ONBOARDING_PATH,
        ADD_BUSINESS_PATH,
        CORE_PAGES.CHECK_LOGIN,
        CORE_PAGES.SETTINGS.CONFIRM_PAYMENT,
        CORE_PAGES.SETTINGS.COMPLETE_PAYMENT,
        CORE_PAGES.SETTINGS.CONFIRM_PLAN,
        CORE_PAGES.SETTINGS.SELECT_PLAN,
    ];
    return paths.some((name) => path.startsWith(name));
};

export const useSystemBanner = () => {
    const pathname = useLocation().pathname;
    const hideBanner = hideBannerByPath(pathname);
    const { businesses } = useUserInfo();
    const { data: subscriptionDetails, isLoading: loadingSubscriptionDetails } =
        useQuery({
            queryFn: getSubscriptionStatus,
            queryKey: [SHARED_QUERY_KEY.GET_SUBSCRIPTION_STATUS],
            enabled: !!businesses?.length && !hideBanner,
        });

    const { data, loading } = useGetApiService<BannerInfo>({
        url: API_URL.BUSINESS.BANNER,
        queryName: SHARED_QUERY_KEY.GET_GLOBAL_BANNER_INFO,
        autoExecute: !!subscriptionDetails,
    });

    const { mutate, isPending } = useDismissBannerApi();

    const handleDismiss = useCallback(() => {
        if (!data?.id) return;

        mutate(data?.id);
    }, [data?.id, mutate]);

    const banner: BannerInfo | undefined = useMemo(() => {
        if (
            loading ||
            isPending ||
            loadingSubscriptionDetails ||
            !businesses?.length ||
            hideBanner
        )
            return undefined;
        return getBannerContent(subscriptionDetails, data);
    }, [
        loading,
        isPending,
        loadingSubscriptionDetails,
        subscriptionDetails,
        data,
        businesses?.length,
        hideBanner,
    ]);

    const freeTrialMessage = useMemo(() => {
        if (
            subscriptionDetails?.status !== SubscriptionStatus.FREE_TRIAL ||
            !subscriptionDetails.subscriptionEndDate ||
            hideBanner
        ) {
            return null;
        }

        const format = 'YYYY-MM-DD';
        const endDate = dayjs(subscriptionDetails.subscriptionEndDate).format(
            format,
        );
        const today = dayjs().format(format);
        const remainingDays = dayjs(endDate).diff(today, 'day');

        if (remainingDays < 0) return null;

        const message =
            remainingDays === 1 ? '1 day left' : `${remainingDays} days left`;

        return `You have ${message} on your free trial.`;
    }, [
        subscriptionDetails?.status,
        subscriptionDetails?.subscriptionEndDate,
        hideBanner,
    ]);

    return {
        banner,
        handleDismiss,
        freeTrialMessage,
    };
};

async function getSubscriptionStatus() {
    return Axios.get<SubscriptionStatusDetails>(
        API_URL.SETTINGS.SUBSCRIPTION_STATUS,
    ).then((res) => res.data);
}
